import { PouchSize } from "@./cat-calculations";
import { TFreshExtraProduct, TFreshRecipe } from "@api-clients/subscriptions";
import { ICatAgeAtSignup } from "@api-clients/cats";

/**
 * Wire-only marker for the trio bundle in the price-estimate response.
 * Not a real product — the bundle is delivered as 3 individual pouch SKUs.
 * Lives here (next to the response type) so it doesn't leak into shared product types.
 */
export const NIBBLES_TRIO_BUNDLE_PRODUCT_TITLE = "NIBBLES_TRIO_BUNDLE" as const;

/** Titles that can appear in an IExtraDetail on the price-estimate response. */
export type TPriceEstimateExtraTitle = TFreshExtraProduct | typeof NIBBLES_TRIO_BUNDLE_PRODUCT_TITLE;

import {
  Allergen,
  CatActivityLevel,
  CatBodyType,
  CatBreed,
  CatGender,
  DiscountCodeType,
  PricingPlan,
} from "@/primary-models";


export interface ICatRecipeDetails {
  catId?: number | null;
  catName: string;
  sprinklesQuantity?: number;
  nibblesSalmonTubeQuantity?: number;
  nibblesChickenTubeQuantity?: number;
  /**
   * Trio bundle (1 chicken + 1 salmon + 1 tuna pouch, priced as a unit). Primary cat only.
   * Boolean because composition is fixed — evolve if marketing introduces customisable mixes.
   */
  hasAddedNibblesTrioBundle?: boolean;
  recipeTitleToQuantity: Partial<Record<TFreshRecipe, number>>;
  catAge: ICatAgeAtSignup;
  trialMealPlan: PricingPlan;
  /** The meal plan after the trial period. Defaults to STANDARD */
  ongoingMealPlan?: PricingPlan;
  weightInKg?: number;
  bodyType?: CatBodyType;
  lifestyle?: CatActivityLevel;
  /** Defaults to neutered when omitted. */
  isNeutered?: boolean;
  allergies?: Allergen[];
  /** Used with gender to pick the adult weight a kitten is growing towards. Defaults to a moggie. */
  breed?: CatBreed;
  gender?: CatGender;
}

export interface IGetPriceEstimateRequest {
  /** delivery date for the subscription (YYYY-MM-DD) */
  deliveryDate: string;
  cats: ICatRecipeDetails[];
  discountCode?: string;
  /** User UUID — optional, for debugging price-estimate failures in Datadog. */
  userId?: string;
  /** Legacy customer ID (oldId) — optional, for debugging price-estimate failures in Datadog. */
  legacyCustomerId?: string;
}

/** Total and per day price breakdown */
export interface IBasePriceBreakdown {
  total: string;
  perDay: string;
}

/** ************ SINGLE CAT PRICING BREAKDOWN TYPES ************ */

interface IRecipeDetail<T extends TPriceEstimateExtraTitle | TFreshRecipe> {
  quantity: number;
  productTitle: T;
}

export interface IExtraDetail extends IRecipeDetail<TPriceEstimateExtraTitle> {
  unitPrice: string;
  /** total price for the product. unit price * quantity */
  totalPrice: string;
  /** discounted price for the product. unit price * quantity - discount */
  discountedPrice: string;
}

/** Total, per day and per portion price breakdown for a single cat's meal plan */
export interface ISingleCatMealPlanPriceBreakdown extends IBasePriceBreakdown {
  /** Price per day per portion (1 tray contains 2 portions) */
  perPortion: string;
}

/** Pricing summary for a single cat, including meal plan, extras and overall price */
export interface ISingleCatPricingSummary {
  /** Price of fresh subscription excluding extras. Includes total, per day and per portion */
  mealPlan: ISingleCatMealPlanPriceBreakdown,
  /** Price of extras. Includes total and per day */
  extras: IBasePriceBreakdown,
  /** Price of fresh subscription + extras. Includes total and per day */
  overall: IBasePriceBreakdown,
}

export interface ICatTrialSubscriptionPricingEstimate {
  planType: PricingPlan;
  original: ISingleCatPricingSummary;
  discounted: ISingleCatPricingSummary;
  recipes: IRecipeDetail<TFreshRecipe>[];
  extras: IExtraDetail[];
}

export interface ICatOngoingSubscriptionPricingEstimate {
  planType: PricingPlan;
  original: ISingleCatPricingSummary;
  discounted: ISingleCatPricingSummary;
}

export interface ICatSubscriptionPriceEstimate {
  catId?: number | null;
  catName: string;
  traySize: PouchSize;
  trial: ICatTrialSubscriptionPricingEstimate;
  ongoing: ICatOngoingSubscriptionPricingEstimate;
}

/** ************ TOTALS PRICING BREAKDOWN TYPES ************ */

/** Total, per day and per cat per day price breakdown for totals */
export interface ITotalsPriceBreakdown extends IBasePriceBreakdown {
  perCatPerDay: string;
}

export interface ITotalsMealPlanPriceBreakdown extends ITotalsPriceBreakdown {
  /** Price per day per portion (1 tray contains 2 portions) */
  perPortion: string;
}

export interface ITotalsPricingSummary {
  /** Price of fresh subscription excluding extras for all cats. Includes total, per day and per portion */
  mealPlan: ITotalsMealPlanPriceBreakdown,
  /** Price of extras for all cats. Includes total and per day */
  extras: ITotalsPriceBreakdown,
  /** Price of fresh subscription + extras for all cats + delivery. Includes total and per day */
  overall: ITotalsPriceBreakdown,
  delivery: ITotalsPriceBreakdown,
}

export interface ITotalsSubscriptionPriceEstimate {
  original: ITotalsPricingSummary;
  discounted: ITotalsPricingSummary;
}

export interface ITotalsPriceEstimate {
  /** Pricing breakdown for the trial period
   * Original prices include free sprinkles but no other discounts are applied.
   * Discounted prices include the free sprinkles and promotional discount. Multi-cat discounts are not applied.
   */
  trial: ITotalsSubscriptionPriceEstimate;
  /** Pricing breakdown for the ongoing subscription after trial ends.
   * Defaults to STANDARD plan pricing.
   * Original prices include delivery fee and no discounts are applied.
   * Free sprinkles are removed and extras are fully priced.
   * Discounted prices include the delivery fee and multicat discount.
   */
  ongoing: ITotalsSubscriptionPriceEstimate;
}

export interface IDiscountDetail {
  code: string;
  discountType: DiscountCodeType;
  value: string;
}

export interface GTMProduct {
  name: string;
  id: string;
  price: string;
  brand: string;
  quantity: number;
}

export interface IGetPriceEstimateResponse {
  cats: ICatSubscriptionPriceEstimate[];
  totals: ITotalsPriceEstimate;
  /** delivery date for the subscription (YYYY-MM-DD) */
  deliveryDate: string;
  discount?: IDiscountDetail;
  gtmProducts?: GTMProduct[];
}
